import { parseEntryParams, parseGiftCardParam } from '../../features/editor/entry-params';

/**
 * A gift-card code handed to the funnel in its entry link, held until the
 * reservation it is meant for exists.
 *
 * The reward shop (`reward.imt.fi`) links customers in as
 * `/?trip=201&adults=2&children=1&giftCard=ABCDEFGHJK`, so they need not retype
 * the code they were just given. Until the booking is paid that code is a live,
 * unspent secret, so it spends as little time in the address bar as possible:
 * {@link captureGiftCardLink} reads it and takes it out before Sentry or the
 * router start. Nothing downstream ever sees it — not the landing → editor
 * hand-off (`queryParamsHandling: 'preserve'`), not the editor's own `merge`
 * rewrites, not browser history, analytics page views or error reports — so none
 * of them can carry it along or put it back.
 *
 * Held in `sessionStorage` rather than memory because applying it may have to
 * wait for a later page load (a transient failure), and scoped to the trip the
 * link was for: a customer who ends up booking something else was not given a
 * code for that. Applying it is `GiftCardLinkService`'s job.
 */

const STORAGE_KEY = 'imtFunnelGiftCardLink';
const PARAM = 'giftCard';

interface PendingGiftCardLink {
  /** Normalised, see {@link parseGiftCardParam}. */
  code: string;
  /** The trip the link was for. */
  tripId: number;
}

/** Where the code is held when storage is blocked (private mode, cookies off):
 *  for this page load only, which still covers the landing → editor hand-off. */
let heldInMemory: PendingGiftCardLink | null = null;

/**
 * Take a gift-card code out of the entry URL and hold on to it.
 *
 * CALLED ONCE AT BOOTSTRAP, before `Sentry.init` and the router: the router
 * would copy the code into every navigation that preserves or merges params,
 * and Sentry's history breadcrumbs would record the URL it was removed from.
 * `replaceState` rather than a navigation, so the address with the code in it
 * leaves no history entry to go back to.
 *
 * A code the funnel cannot use — blank, too long, or on a link that names no
 * trip — is removed all the same, and not kept. Nor is a code held from an
 * earlier link: this link is the latest word on which code the customer means,
 * and the earlier one would otherwise still go on the next reservation for its
 * trip. Only a URL with no `giftCard` at all leaves a held code alone.
 *
 * Returns null once the URL is clean. When the browser refuses the rewrite (a
 * sandboxed frame can), returns the URL without the code instead, and the
 * caller must load that rather than start on an address still carrying it — the
 * router would copy it onward and Sentry would record it. The code is stored by
 * then (sessionStorage survives the reload), and the URL handed back has no
 * `giftCard` in it, so the reload passes straight through here.
 */
export function captureGiftCardLink(): string | null {
  const { pathname, search, hash } = window.location;
  const remaining = withoutGiftCardParam(search);
  if (remaining === null) return null;

  const entry = parseEntryParams(new URLSearchParams(search));
  if (entry.giftCard !== null && entry.tripId !== null) {
    storePendingGiftCardLink({ code: entry.giftCard, tripId: entry.tripId });
  } else {
    clearPendingGiftCardLink();
  }

  const cleanUrl = `${pathname}${remaining}${hash}`;
  try {
    window.history.replaceState(window.history.state, '', cleanUrl);
    return null;
  } catch {
    return cleanUrl;
  }
}

/**
 * `search` with every `giftCard` param removed, or null when it has none.
 *
 * Cut out of the raw string rather than rebuilt through `URLSearchParams`, which
 * would re-encode every other param on the way (`returnTo=/haku/?q=…` comes back
 * as `returnTo=%2Fhaku%2F%3Fq%3D…`), so the rest of the link reaches the router
 * exactly as it was sent.
 */
export function withoutGiftCardParam(search: string): string | null {
  const pairs = search
    .replace(/^\?/, '')
    .split('&')
    .filter((pair) => pair !== '');
  const kept = pairs.filter((pair) => paramName(pair) !== PARAM);
  if (kept.length === pairs.length) return null;
  return kept.length > 0 ? `?${kept.join('&')}` : '';
}

/** A query pair's decoded name, read the way `URLSearchParams` would. */
function paramName(pair: string): string {
  const name = pair.split('=', 1)[0].replace(/\+/g, ' ');
  try {
    return decodeURIComponent(name);
  } catch {
    return name;
  }
}

/** Hold `link` until {@link clearPendingGiftCardLink}, replacing any earlier one. */
export function storePendingGiftCardLink(link: PendingGiftCardLink): void {
  try {
    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(link));
    heldInMemory = null;
  } catch {
    heldInMemory = link;
  }
}

/**
 * The held code, or null.
 *
 * Re-validated on the way out, as `readEntryReferrer` does: `sessionStorage`
 * outlives a deploy, and a value this build would not have accepted from a link
 * is not one to send.
 */
export function readPendingGiftCardLink(): PendingGiftCardLink | null {
  if (heldInMemory !== null) return heldInMemory;
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    return raw === null ? null : parseStored(raw);
  } catch {
    return null;
  }
}

export function clearPendingGiftCardLink(): void {
  heldInMemory = null;
  try {
    window.sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // Blocked storage: nothing was written there to clear.
  }
}

function parseStored(raw: string): PendingGiftCardLink | null {
  let value: unknown;
  try {
    value = JSON.parse(raw);
  } catch {
    return null;
  }
  if (value === null || typeof value !== 'object') return null;
  const { code, tripId } = value as Record<string, unknown>;
  const normalised = typeof code === 'string' ? parseGiftCardParam(code) : null;
  if (normalised === null || typeof tripId !== 'number' || !Number.isInteger(tripId)) return null;
  return tripId > 0 ? { code: normalised, tripId } : null;
}
